import type RootStore from 'stores/RootStore'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'
import hasSecondDebtor from 'utils/formDependencies/conditions/hasSecondDebtor'
import { DELIVERY_METHOD } from 'constants/documentDelivery'
import type { DeliveryMethod } from 'constants/documentDelivery'
import type {
  DocumentDeliverySession,
  DocumentKey
} from 'interfaces/documentDelivery.interface'
import { buildDocumentDeliveryPayload } from 'utils/documentDeliveryPayload'

// Tracked values are spelled out so they stay readable in Amplitude.
const TRACKED_DELIVERY_METHOD: Record<DeliveryMethod, string> = {
  [DELIVERY_METHOD.EMAIL]: DELIVERY_METHOD.EMAIL,
  [DELIVERY_METHOD.POST]: DELIVERY_METHOD.POST,
  [DELIVERY_METHOD.PER_DOCUMENT]: 'per_document'
}

export const getDocumentDeliverySelection = (
  session: DocumentDeliverySession,
  hasSecond: boolean
) => {
  const { deliveryMethod } = session

  if (!deliveryMethod) {
    return {}
  }

  const trackedMethod = TRACKED_DELIVERY_METHOD[deliveryMethod]
  const payload = buildDocumentDeliveryPayload(session, hasSecond)

  if (!payload) {
    return { deliveryMethod: trackedMethod }
  }

  const { primary, secondary } = payload.debtors

  // Always the complete backend set per debtor, also when it follows from the main choice or the first debtor.
  console.log('Document delivery payload:', {
    deliveryMethod: trackedMethod,
    primary: primary.documentDeliveryPreferences,
    ...(secondary && {
      isSecondSameAsFirst: secondary.documentDeliveryPreferencesFollowPrimary,
      secondary: secondary.documentDeliveryPreferences
    })
  })

  return {
    deliveryMethod: trackedMethod,
    primary: primary.documentDeliveryPreferences,
    ...(secondary && {
      isSecondSameAsFirst: secondary.documentDeliveryPreferencesFollowPrimary,
      secondary: secondary.documentDeliveryPreferences
    })
  }
}

/** Fired right before finalization; stays silent when the user never chose an option. */
export const trackDocumentDeliverySelection = ({
  traversalSession: { documentDelivery },
  traversal: { data }
}: RootStore) => {
  if (!documentDelivery.deliveryMethod) {
    return
  }

  trackInAmplitude(
    AmplitudeEvent.DocumentDeliverySelection,
    getDocumentDeliverySelection(documentDelivery, hasSecondDebtor(data))
  )
}

/** The "ändern" link was clicked. */
export const trackDocumentDeliveryChangeLinkClicked = () =>
  trackInAmplitude(AmplitudeEvent.DocumentDeliveryChangeLinkClicked)

/** The checkbox shows its error because no option is selected. */
export const trackDocumentDeliveryOptionMissing = (
  debtorCount: number,
  trigger: 'submit' | 'deselect'
) =>
  trackInAmplitude(AmplitudeEvent.DocumentDeliveryOptionMissing, {
    debtorCount,
    trigger
  })

/** Errors are shown for document choices left open after picking "Einzeln anpassen". */
export const trackDocumentDeliveryChoiceMissing = (
  missing: Record<'primary' | 'secondary', DocumentKey[]>,
  secondDebtor?: { isSameAsFirst: boolean }
) =>
  trackInAmplitude(AmplitudeEvent.DocumentDeliveryChoiceMissing, {
    missingPrimary: missing.primary,
    ...(secondDebtor && {
      isSecondSameAsFirst: secondDebtor.isSameAsFirst,
      // A taken-over selection has no choices of its own that could be missing.
      ...(!secondDebtor.isSameAsFirst && {
        missingSecondary: missing.secondary
      })
    })
  })
